import { useCallback, useMemo } from 'react'

import EnterpriseIcon, {
  CancelCircleIcon,
  CheckCircleIcon,
} from '@enterprise-ui/icons'

import { NovaTable } from '@dlm/common'

import RulesConfigurationEdit from '../../RulesConfigurationEdit/RulesConfigurationEdit'

import useUser from '../../../common/hooks/useUser'

import { startCase } from '../../../common/util/stringUtil'
import { toWrappedText } from '../../../common/util/wrappedTextCell'

export const defaultColumnDefs = [
  {
    headerName: 'Category',
    field: 'rule_category',
    width: 180,
    sortable: true,
  },
  {
    headerName: 'Type',
    field: 'rule_type',
    width: 400,
    sortable: true,
  },
  {
    headerName: 'Rule',
    field: 'rule',
    isRowHeader: true,
  },
  {
    headerName: 'Description',
    field: 'short_description',
    width: 280,
  },
  {
    headerName: 'Includes',
    field: 'includes',
    width: 130,
  },
  {
    headerName: 'Value List',
    field: 'value_list',
    width: 550,
  },
  {
    headerName: 'Active',
    field: 'active_flag',
    width: 100,
  },
  {
    headerName: 'Updated By',
    field: 'update_by',
    width: 200,
    sortable: true,
  },
  {
    headerName: 'Updated Date',
    field: 'update_dt',
    width: 200,
    sortable: true,
  },
]

const RulesConfigurationTable = ({
  ruleData,
  ruleCount,
  onRefresh,
  tableName,
  ...restProps
}) => {
  const {
    access: { isAdmin },
  } = useUser()

  const mapRuleData = useCallback(
    (rules = []) => {
      const toIcon = (value) => {
        const properties = value
          ? { icon: CheckCircleIcon, color: 'green' }
          : { icon: CancelCircleIcon, color: 'red' }

        return {
          cellValue: value,
          cellDisplay: (
            <EnterpriseIcon icon={properties.icon} color={properties.color} />
          ),
        }
      }

      const mappedRules = []

      rules?.forEach((rule) => {
        mappedRules.push({
          edit_button: isAdmin && {
            cellValue: null,
            cellDisplay: (
              <RulesConfigurationEdit
                id={`${rule.rule}-${rule.rule_type}`}
                onRefresh={onRefresh}
              />
            ),
          },
          rule_category: toWrappedText(rule.rule_category || 'N/A'),
          rule: toWrappedText(startCase(rule.rule)),
          rule_type: toWrappedText(rule.rule_type),
          includes: toWrappedText(
            rule.rule_values?.include_flag ? 'Included' : 'Excluded',
          ),
          value_list: toWrappedText(
            rule.rule_values?.value_list?.join(', ') || 'N/A',
          ),
          short_description: toWrappedText(rule.short_description),
          active_flag: toIcon(rule.active_flag),
          update_by: toWrappedText(rule.update_by || 'N/A'),
          update_dt: toWrappedText(
            rule.update_dt
              ? new Date(rule.update_dt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                })
              : 'N/A',
          ),
        })
      })

      return mappedRules
    },
    [isAdmin, onRefresh],
  )

  const rowData = useMemo(() => mapRuleData(ruleData), [ruleData, mapRuleData])

  const columnDefs = []
  if (isAdmin) {
    columnDefs.push({
      headerName: 'Edit',
      field: 'edit_button',
      width: 75,
    })
  }
  columnDefs.push(...defaultColumnDefs)

  return (
    <form onSubmit={(event) => event.preventDefault()}>
      <NovaTable
        name={tableName || 'Configuration'}
        aria-label={`${tableName || 'Configuration'} Table`}
        showHeader
        scrollableWidth={true}
        columnDefs={columnDefs}
        rowData={rowData}
        rowCount={ruleCount}
        // TODO - Remove pagination once supported
        pageNum={1}
        pageSize={ruleCount}
        onPaginationChange={() => {}}
        {...restProps}
      />
    </form>
  )
}

export default RulesConfigurationTable
